/* ============================================================================
   CHANVRERY — SURCOUCHES JURIDIQUES : VÉRIFICATION D'ÂGE ET CONSENTEMENT
   ============================================================================

   Deux composants natifs, sans dépendance ni build, comme le reste du thème :

     <ch-age-gate>          barrière 18+ bloquante   snippets/ch-overlay-age.liquid
     <ch-cookies-consent>   bandeau + préférences    snippets/ch-overlay-cookies.liquid

   🔴 LE CONSENTEMENT EST RÉEL
   Chaque choix appelle `Shopify.customerPrivacy.setTrackingConsent()`. Sans
   cet appel, Shopify continue de charger ses traceurs et le bandeau ne vaut
   juridiquement rien. L'état déjà exprimé est relu au chargement avec
   `currentVisitorConsent()`.

   Ce fichier peut être évalué deux fois sans dommage (deux snippets, un seul
   script) : les définitions sont gardées par `customElements.get`.
   ========================================================================= */

(function () {
  'use strict';

  /* ==========================================================================
     OUTILS COMMUNS
     ====================================================================== */

  var COOKIE_AGE = 'ch_age';
  var COOKIE_COOKIES = 'ch_consentement';
  var CATEGORIES = ['analytics', 'preferences', 'marketing'];

  function lireCookie(nom) {
    try {
      var trouve = document.cookie.match(new RegExp('(?:^|;\\s*)' + nom + '=([^;]*)'));
      return trouve ? decodeURIComponent(trouve[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function ecrireCookie(nom, valeur, jours) {
    try {
      var secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        nom +
        '=' +
        encodeURIComponent(valeur) +
        '; Max-Age=' +
        Math.round(jours * 86400) +
        '; Path=/; SameSite=Lax' +
        secure;
    } catch (e) {
      /* Stockage refusé : le choix ne survivra pas au rechargement, mais la
         session en cours reste correcte. Rien à rattraper ici. */
    }
  }

  function effacerCookie(nom) {
    try {
      document.cookie = nom + '=; Max-Age=0; Path=/; SameSite=Lax';
    } catch (e) {
      /* Idem. */
    }
  }

  var SELECTEUR_FOCUS =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function elementsFocusables(racine) {
    return Array.prototype.filter.call(racine.querySelectorAll(SELECTEUR_FOCUS), function (element) {
      return element.offsetParent !== null || element === document.activeElement;
    });
  }

  /* `inert` sur tout ce qui n'est pas la surcouche : c'est ce qui garantit
     qu'AUCUN élément de la page derrière n'est atteignable à la tabulation —
     un piège de focus seul laisse encore passer la barre d'outils du
     navigateur vers le document. On remonte jusqu'à <body> pour couvrir aussi
     le cas d'une surcouche imbriquée dans un hôte. */
  function isoler(surcouche) {
    var isoles = [];
    var noeud = surcouche;
    while (noeud && noeud.parentElement && noeud.parentElement !== document.documentElement) {
      Array.prototype.forEach.call(noeud.parentElement.children, function (frere) {
        if (frere === noeud || frere.inert) return;
        frere.inert = true;
        isoles.push(frere);
      });
      noeud = noeud.parentElement;
    }
    return isoles;
  }

  function rendre(isoles) {
    isoles.forEach(function (element) {
      element.inert = false;
    });
  }

  /* Piège de tabulation. Filet de sécurité pour les navigateurs sans `inert`
     et pour les éléments focalisables ajoutés après l'isolement. */
  function piegerTabulation(evenement, conteneur) {
    if (evenement.key !== 'Tab') return;

    var liste = elementsFocusables(conteneur);
    if (liste.length === 0) return;

    var premier = liste[0];
    var dernier = liste[liste.length - 1];

    if (!conteneur.contains(document.activeElement)) {
      evenement.preventDefault();
      premier.focus();
    } else if (evenement.shiftKey && document.activeElement === premier) {
      evenement.preventDefault();
      dernier.focus();
    } else if (!evenement.shiftKey && document.activeElement === dernier) {
      evenement.preventDefault();
      premier.focus();
    }
  }

  /* Le panneau part parfois de `visibility`/`display` non peints : un focus()
     synchrone ne prend pas. Même parade que le tiroir de navigation
     (sections/header.liquid) : on réessaie sur un budget borné. */
  function poserLeFocus(cible) {
    if (!cible) return;
    var trames = 12;
    var essai = function () {
      cible.focus();
      if (document.activeElement !== cible && trames-- > 0) requestAnimationFrame(essai);
    };
    requestAnimationFrame(essai);
  }

  /* ==========================================================================
     API SHOPIFY DE CONFIDENTIALITÉ CLIENT
     ======================================================================
     `Shopify.customerPrivacy` n'est pas là au chargement : il faut le demander
     avec `Shopify.loadFeatures`. Tant qu'il n'est pas prêt, on empile les
     appels plutôt que de les perdre.
     ====================================================================== */

  var filePrivacy = [];
  var etatPrivacy = 'inconnu'; /* inconnu | chargement | pret | indisponible */

  function avecPrivacy(rappel) {
    if (etatPrivacy === 'pret') {
      rappel(window.Shopify.customerPrivacy);
      return;
    }
    if (etatPrivacy === 'indisponible') {
      rappel(null);
      return;
    }

    filePrivacy.push(rappel);
    if (etatPrivacy === 'chargement') return;

    var vider = function (api) {
      var file = filePrivacy;
      filePrivacy = [];
      file.forEach(function (fonction) {
        fonction(api);
      });
    };

    if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.setTrackingConsent) {
      etatPrivacy = 'pret';
      vider(window.Shopify.customerPrivacy);
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      etatPrivacy = 'indisponible';
      vider(null);
      return;
    }

    etatPrivacy = 'chargement';
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (erreur) {
      var api = window.Shopify.customerPrivacy;
      if (erreur || !api || typeof api.setTrackingConsent !== 'function') {
        etatPrivacy = 'indisponible';
        vider(null);
        return;
      }
      etatPrivacy = 'pret';
      vider(api);
    });
  }

  /* Consentement déjà exprimé côté Shopify. Renvoie null si personne n'a
     encore tranché : c'est ce qui décide de l'affichage du bandeau. */
  function consentementShopify(api) {
    if (!api || typeof api.currentVisitorConsent !== 'function') return null;

    var brut;
    try {
      brut = api.currentVisitorConsent();
    } catch (e) {
      return null;
    }
    if (!brut) return null;

    var exprime = CATEGORIES.some(function (categorie) {
      return brut[categorie] === 'yes' || brut[categorie] === 'no';
    });
    if (!exprime) return null;

    var etat = {};
    CATEGORIES.forEach(function (categorie) {
      etat[categorie] = brut[categorie] === 'yes';
    });
    return etat;
  }

  function envoyerAShopify(etat) {
    avecPrivacy(function (api) {
      if (!api) return;
      try {
        api.setTrackingConsent(
          {
            analytics: etat.analytics,
            preferences: etat.preferences,
            marketing: etat.marketing,
            /* Pas de ligne « revente de données » dans la maquette. On l'aligne
               sur la publicité ciblée, seule finalité qui recouvre la
               transmission à des tiers publicitaires. À arbitrer si la boutique
               ouvre aux États-Unis (CCPA). */
            sale_of_data: etat.marketing,
          },
          function (reponse) {
            /* Shopify répond `{ error }` quand l'enregistrement échoue —
               notamment sur `shopify theme dev`, où l'appel à l'API Storefront
               part d'une origine locale et n'aboutit pas. Un échec silencieux
               ici, c'est un bandeau juridiquement inutile : on le dit tout
               haut plutôt que de laisser croire que le choix est appliqué. */
            if (reponse && reponse.error) {
              console.warn('[Chanvrery] Consentement non enregistré par Shopify :', reponse.error);
            }
          }
        );
      } catch (e) {
        /* L'API refuse (fonction non activée sur la boutique). Le choix reste
           mémorisé côté thème, mais il ne pilote alors PAS les traceurs
           Shopify : voir `reglagesBoutique` du lot 3. */
        console.warn('[Chanvrery] API de confidentialité Shopify indisponible :', e && e.message);
      }
    });
  }

  /* ==========================================================================
     API PUBLIQUE DU THÈME
     ======================================================================
     Point d'accroche unique pour tout composant qui doit savoir si une
     catégorie est autorisée — à commencer par l'écran de consentement de la
     carte Google de /pages/boutiques (sections/ch-boutiques-carte.liquid),
     qui charge une iframe déposant un cookie tiers.
     ====================================================================== */

  var consentementCourant = null;
  var abonnes = [];

  function diffuser(etat) {
    consentementCourant = etat;
    abonnes.forEach(function (fonction) {
      try {
        fonction(etat);
      } catch (e) {
        /* Un abonné qui échoue ne doit pas empêcher les suivants. */
      }
    });
    document.dispatchEvent(new CustomEvent('ch:consentement', { detail: etat }));
  }

  window.ChanvreryConsentement = window.ChanvreryConsentement || {
    /* Renvoie l'état des trois catégories, ou null si rien n'a été choisi. */
    etat: function () {
      return consentementCourant;
    },
    /* Vrai UNIQUEMENT si la catégorie a été explicitement autorisée. */
    autorise: function (categorie) {
      return !!(consentementCourant && consentementCourant[categorie] === true);
    },
    surChangement: function (fonction) {
      if (typeof fonction === 'function') abonnes.push(fonction);
    },
    ouvrirPreferences: function () {
      var hote = document.querySelector('ch-cookies-consent');
      if (hote && typeof hote.ouvrirPanneau === 'function') hote.ouvrirPanneau();
    },
  };

  /* ==========================================================================
     VÉRIFICATION D'ÂGE — nœuds Figma 112:8381, 112:8206, 112:8568
     ====================================================================== */

  class ChAgeGateElement extends HTMLElement {
    connectedCallback() {
      this.racine = document.documentElement;
      this.duree = parseFloat(this.dataset.dureeJours) || 30;
      this.vueQuestion = this.querySelector('[data-ch-age-vue="question"]');
      this.vueRefus = this.querySelector('[data-ch-age-vue="refus"]');
      this.isoles = [];

      this.surTouche = this.surTouche.bind(this);
      this.addEventListener('click', this.surClic.bind(this));

      /* Le focus entre dans la modale dès le chargement : c'est un dialogue
         modal bloquant, laisser le focus sur <body> obligerait la personne au
         clavier à deviner où elle est. Rien ne défile, la surcouche est fixe. */
      this.appliquer(this.racine.dataset.chAge || 'demande');
    }

    disconnectedCallback() {
      this.liberer();
    }

    surClic(evenement) {
      var cible = evenement.target;
      if (!(cible instanceof Element)) return;

      if (cible.closest('[data-ch-age-accepter]')) {
        ecrireCookie(COOKIE_AGE, 'accepte', this.duree);
        this.appliquer('accepte');
        return;
      }

      if (cible.closest('[data-ch-age-refuser]')) {
        /* Le refus est mémorisé lui aussi : sans ça, un simple rechargement
           suffirait à repasser devant la barrière. */
        ecrireCookie(COOKIE_AGE, 'refuse', this.duree);
        this.appliquer('refuse');
        return;
      }

      if (cible.closest('[data-ch-age-corriger]')) {
        effacerCookie(COOKIE_AGE);
        this.appliquer('demande');
        return;
      }

      if (cible.closest('[data-ch-age-quitter]')) this.quitter();
    }

    /* Un seul commutateur : l'attribut `data-ch-age` sur <html>. Le CSS lit
       cet attribut, le script en ligne du snippet le pose avant la première
       peinture, et cette méthode le fait évoluer au clic. */
    appliquer(etat, deplacerLeFocus) {
      this.racine.dataset.chAge = etat;

      if (etat === 'accepte') {
        this.liberer();
        document.dispatchEvent(new CustomEvent('ch:age-verifie'));
        return;
      }

      this.racine.classList.add('ch-overlay-bloque');
      if (this.isoles.length === 0) this.isoles = isoler(this);
      document.addEventListener('keydown', this.surTouche);

      var vue = etat === 'refuse' ? this.vueRefus : this.vueQuestion;
      var titre = vue ? vue.querySelector('h2') : null;
      if (titre && titre.id) this.setAttribute('aria-labelledby', titre.id);

      if (deplacerLeFocus === false) return;
      poserLeFocus(vue ? vue.querySelector(SELECTEUR_FOCUS) : null);
    }

    liberer() {
      this.racine.classList.remove('ch-overlay-bloque');
      document.removeEventListener('keydown', this.surTouche);
      rendre(this.isoles);
      this.isoles = [];
    }

    surTouche(evenement) {
      /* Pas d'échappement au clavier : `Échap` ne doit PAS refermer une
         barrière légale. Seule la tabulation est encadrée. */
      var vue = this.racine.dataset.chAge === 'refuse' ? this.vueRefus : this.vueQuestion;
      if (vue) piegerTabulation(evenement, vue);
    }

    quitter() {
      var lien = this.dataset.lienSortie;
      if (lien) {
        window.location.href = lien;
        return;
      }
      /* `window.close()` n'aboutit que sur un onglet ouvert par un script.
         Sinon on quitte quand même la boutique, vers une page vide : mieux
         qu'un bouton qui ne fait rien, et sans inventer de destination. */
      window.close();
      window.setTimeout(function () {
        window.location.replace('about:blank');
      }, 150);
    }
  }

  /* ==========================================================================
     CONSENTEMENT COOKIES — nœuds Figma 112:9223, 112:9816, 112:9593
     ====================================================================== */

  class ChCookiesConsentElement extends HTMLElement {
    connectedCallback() {
      this.duree = parseFloat(this.dataset.dureeJours) || 180;
      this.bandeau = this.querySelector('[data-ch-cookies-bandeau]');
      this.panneau = this.querySelector('[data-ch-cookies-panneau]');
      this.interrupteurs = Array.prototype.slice.call(this.querySelectorAll('[data-ch-cookies-categorie]'));
      this.isoles = [];
      this.declencheur = null;

      this.surTouche = this.surTouche.bind(this);
      this.surClicDocument = this.surClicDocument.bind(this);
      this.surAgeVerifie = this.surAgeVerifie.bind(this);

      this.addEventListener('click', this.surClicHote.bind(this));
      /* Le lien de réouverture vit dans le pied de page, hors de cet hôte :
         on écoute donc le document entier. */
      document.addEventListener('click', this.surClicDocument);
      document.addEventListener('ch:age-verifie', this.surAgeVerifie);

      this.initialiser();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.surClicDocument);
      document.removeEventListener('ch:age-verifie', this.surAgeVerifie);
      document.removeEventListener('keydown', this.surTouche);
      if (this.surRedimensionnement) {
        window.removeEventListener('resize', this.surRedimensionnement);
        this.surRedimensionnement = null;
      }
      rendre(this.isoles);
      this.isoles = [];
    }

    /* --- Démarrage --------------------------------------------------------- */

    initialiser() {
      var local = this.lireChoixLocal();

      avecPrivacy(
        function (api) {
          var distant = consentementShopify(api);
          /* Shopify fait foi : c'est lui qui pilote réellement les traceurs.
             Le cookie du thème n'est qu'un doublon de confort, utile quand
             l'API n'est pas activée sur la boutique. */
          var etat = distant || local;

          if (etat) {
            this.refleter(etat);
            diffuser(etat);
            /* Le choix local et le choix Shopify peuvent avoir divergé (cookie
               du thème effacé, changement de navigateur) : on les réaligne. */
            if (distant) this.ecrireChoixLocal(distant);
            else envoyerAShopify(local);
            return;
          }

          this.refleter({ analytics: false, preferences: false, marketing: false });
          this.montrerBandeau();
        }.bind(this)
      );
    }

    lireChoixLocal() {
      var brut = lireCookie(COOKIE_COOKIES);
      if (!brut) return null;
      try {
        var lu = JSON.parse(brut);
        var etat = {};
        CATEGORIES.forEach(function (categorie) {
          etat[categorie] = lu[categorie] === true;
        });
        return etat;
      } catch (e) {
        return null;
      }
    }

    ecrireChoixLocal(etat) {
      ecrireCookie(COOKIE_COOKIES, JSON.stringify(etat), this.duree);
    }

    /* --- Affichage --------------------------------------------------------- */

    montrerBandeau() {
      if (!this.bandeau) return;
      /* Jamais par-dessus la vérification d'âge : tant que la barrière est
         debout, le bandeau attend. Il est révélé par `ch:age-verifie`. */
      if (document.documentElement.dataset.chAge && document.documentElement.dataset.chAge !== 'accepte') {
        this.bandeauEnAttente = true;
        return;
      }
      this.bandeauEnAttente = false;
      this.bandeau.hidden = false;
      this.mesurerBandeau();
      if (!this.surRedimensionnement) {
        this.surRedimensionnement = this.mesurerBandeau.bind(this);
        window.addEventListener('resize', this.surRedimensionnement);
      }
    }

    /* Le bandeau mobile occupe la moitié de l'écran et recouvre les barres
       collantes du bas — la barre d'achat de la fiche produit en premier.
       On publie sa hauteur pour que le CSS puisse les relever d'autant. */
    mesurerBandeau() {
      if (!this.bandeau || this.bandeau.hidden) return;
      var hauteur = Math.round(this.bandeau.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--ch-cookies-hauteur', hauteur + 'px');
      document.documentElement.classList.add('ch-cookies-visible');
    }

    cacherBandeau() {
      this.bandeauEnAttente = false;
      if (this.bandeau) this.bandeau.hidden = true;
      document.documentElement.classList.remove('ch-cookies-visible');
      document.documentElement.style.removeProperty('--ch-cookies-hauteur');
      if (this.surRedimensionnement) {
        window.removeEventListener('resize', this.surRedimensionnement);
        this.surRedimensionnement = null;
      }
    }

    surAgeVerifie() {
      if (this.bandeauEnAttente) this.montrerBandeau();
    }

    refleter(etat) {
      this.interrupteurs.forEach(function (entree) {
        entree.checked = etat[entree.dataset.chCookiesCategorie] === true;
      });
    }

    /* --- Panneau ----------------------------------------------------------- */

    ouvrirPanneau() {
      if (!this.panneau) return;
      this.declencheur = document.activeElement;
      /* Le bandeau disparaît pendant que le panneau est ouvert : la maquette
         du panneau (112:9593) ne le montre pas derrière. */
      this.cacherBandeau();
      this.panneau.hidden = false;
      document.documentElement.classList.add('ch-overlay-bloque');
      this.isoles = isoler(this.panneau);
      document.addEventListener('keydown', this.surTouche);
      poserLeFocus(this.panneau.querySelector(SELECTEUR_FOCUS));
    }

    fermerPanneau() {
      if (!this.panneau) return;
      this.panneau.hidden = true;
      document.documentElement.classList.remove('ch-overlay-bloque');
      rendre(this.isoles);
      this.isoles = [];
      document.removeEventListener('keydown', this.surTouche);

      /* Fermer sans choisir n'est PAS un consentement : le bandeau revient. */
      if (!consentementCourant) this.montrerBandeau();

      if (this.declencheur && document.contains(this.declencheur)) this.declencheur.focus();
      this.declencheur = null;
    }

    surTouche(evenement) {
      if (evenement.key === 'Escape') {
        this.fermerPanneau();
        return;
      }
      piegerTabulation(evenement, this.panneau);
    }

    /* --- Choix ------------------------------------------------------------- */

    surClicHote(evenement) {
      var cible = evenement.target;
      if (!(cible instanceof Element)) return;

      if (cible.closest('[data-ch-cookies-accepter]')) {
        this.enregistrer({ analytics: true, preferences: true, marketing: true });
        return;
      }
      if (cible.closest('[data-ch-cookies-refuser]')) {
        this.enregistrer({ analytics: false, preferences: false, marketing: false });
        return;
      }
      if (cible.closest('[data-ch-cookies-enregistrer]')) {
        var etat = {};
        this.interrupteurs.forEach(function (entree) {
          etat[entree.dataset.chCookiesCategorie] = entree.checked;
        });
        CATEGORIES.forEach(function (categorie) {
          if (typeof etat[categorie] !== 'boolean') etat[categorie] = false;
        });
        this.enregistrer(etat);
        return;
      }
      if (cible.closest('[data-ch-cookies-fermer]')) this.fermerPanneau();
      /* Un clic sur le voile ferme le panneau, comme partout ailleurs. */
      if (cible === this.panneau) this.fermerPanneau();
    }

    surClicDocument(evenement) {
      var cible = evenement.target;
      if (!(cible instanceof Element)) return;
      var ouvrant = cible.closest('[data-ch-cookies-ouvrir]');
      if (!ouvrant) return;
      evenement.preventDefault();
      this.ouvrirPanneau();
    }

    enregistrer(etat) {
      this.refleter(etat);
      this.ecrireChoixLocal(etat);
      envoyerAShopify(etat);
      diffuser(etat);

      this.cacherBandeau();
      if (this.panneau && !this.panneau.hidden) this.fermerPanneau();
    }
  }

  if (!window.customElements.get('ch-age-gate')) {
    window.customElements.define('ch-age-gate', ChAgeGateElement);
  }
  if (!window.customElements.get('ch-cookies-consent')) {
    window.customElements.define('ch-cookies-consent', ChCookiesConsentElement);
  }
})();
